import { inject, Injectable, isDevMode, OnDestroy, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval, Subscription } from 'rxjs';

const CHUNK_RELOAD_KEY = 'elite:chunk-error-reload';
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

/** Drives the single hlm-alert-dialog mounted by AppUpdatePromptComponent. */
export interface AppUpdatePrompt {
  header: string;
  message: string;
  acceptLabel: string;
  rejectLabel?: string;
  accept: () => void;
  reject?: () => void;
}

/**
 * Listens for service worker updates, activates new versions, and reloads the page.
 * Also handles chunk-load errors from unhandled promise rejections.
 * Performs periodic update checks and checks when user returns to the tab.
 * Registered as a root provider so it lives for the entire app lifetime.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly swUpdate = inject(SwUpdate);
  private subscriptions: Subscription[] = [];

  /** Non-null while a confirmation dialog should be showing. */
  readonly prompt = signal<AppUpdatePrompt | null>(null);

  constructor() {
    if (!this.swUpdate.isEnabled) {
      if (isDevMode()) {
        console.info('[AppUpdate] Service Worker is not enabled in dev mode. Updates will not be checked.');
      } else {
        console.warn('[AppUpdate] Service Worker is not enabled. Updates will not be checked.');
      }
      return;
    }

    console.info('[AppUpdate] Service Worker enabled, initializing update checks...');

    // Wait for SW to be stable before first check
    this.waitForSwStable().then(() => {
      this.initializeUpdateChecks();
    });

    // Handle chunk-load errors (unhandled promise rejections)
    this.setupChunkErrorListener();
  }

  /**
   * Waits for the service worker to become stable before checking for updates.
   * This ensures the SW is fully registered and ready.
   */
  private async waitForSwStable(): Promise<void> {
    // Give the SW time to register (registrationStrategy: 'registerWhenStable:30000')
    // Wait up to 35 seconds for SW to become available
    const maxWaitTime = 35000;
    const checkInterval = 1000;
    let waited = 0;

    while (!this.swUpdate.isEnabled && waited < maxWaitTime) {
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }

    if (this.swUpdate.isEnabled) {
      console.info('[AppUpdate] Service Worker is now enabled.');
    } else {
      console.warn('[AppUpdate] Service Worker did not become enabled within timeout.');
    }
  }

  /**
   * Initializes all update check mechanisms:
   * - Initial check
   * - Periodic checks (every 30 minutes)
   * - Visibility-based checks (when user returns to tab)
   */
  private initializeUpdateChecks(): void {
    // Subscribe to version updates
    this.subscriptions.push(
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe((evt) => {
          console.info(
            `[AppUpdate] Update available: ${evt.currentVersion.hash.slice(0, 8)} → ${evt.latestVersion.hash.slice(0, 8)}`,
          );
          this.promptForUpdate();
        }),
    );

    // Handle unrecoverable errors
    this.subscriptions.push(
      this.swUpdate.unrecoverable.subscribe((event) => {
        console.error('[AppUpdate] Unrecoverable error:', event.reason);
        this.promptForReload('An error occurred that requires reloading the page. Please save your work and refresh.');
      }),
    );

    // Initial check for updates
    void this.checkForUpdate();

    // Periodic checks every 30 minutes
    this.subscriptions.push(
      interval(UPDATE_CHECK_INTERVAL_MS).subscribe(() => {
        void this.checkForUpdate();
      }),
    );

    // Check when user returns to the tab (visibility change)
    this.setupVisibilityCheck();
  }

  /**
   * Checks for updates. Safe to call multiple times.
   */
  private async checkForUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;

    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      if (updateFound) {
        console.info('[AppUpdate] Update found during check.');
      }
    } catch (err) {
      console.error('[AppUpdate] Failed to check for updates:', err);
    }
  }

  /**
   * Sets up a listener for visibility changes.
   * When the user returns to the tab, check for updates.
   */
  private setupVisibilityCheck(): void {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.debug('[AppUpdate] Tab became visible, checking for updates...');
        void this.checkForUpdate();
      }
    });
  }

  /**
   * Prompts the user to update when a new version is ready.
   */
  private promptForUpdate(): void {
    this.prompt.set({
      header: 'Update Available',
      message: 'A new version is available. Update now for the latest features and improvements.',
      acceptLabel: 'Update Now',
      rejectLabel: 'Later',
      accept: () => {
        this.prompt.set(null);
        void this.activateAndReload();
      },
      reject: () => {
        this.prompt.set(null);
        console.info('[AppUpdate] Update postponed by user.');
      },
    });
  }

  /**
   * Prompts the user to reload due to an unrecoverable error.
   */
  private promptForReload(message: string): void {
    this.prompt.set({
      header: 'Application Error',
      message,
      acceptLabel: 'Reload',
      accept: () => {
        this.prompt.set(null);
        window.location.reload();
      },
    });
  }

  /**
   * Activates the new version and reloads the page.
   */
  private async activateAndReload(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
    } catch (err) {
      console.error('[AppUpdate] Failed to activate update:', err);
      return;
    }
    // Reload to load the new app version with fresh chunk hashes
    window.location.reload();
  }

  /**
   * Listens for unhandled promise rejections that indicate chunk-load failures.
   * These occur when the browser tries to load a stale chunk hash after a deployment.
   */
  private setupChunkErrorListener(): void {
    if (isDevMode()) return;

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      if (!this.isChunkLoadError(event.reason)) return;
      if (!this.canReload()) return;

      console.warn('[AppUpdate] Stale chunk detected — reloading to fetch latest version.');
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
    });
  }

  /** Detects dynamic-import failures caused by stale chunk hashes after a deployment. */
  private isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return /loading chunk|ChunkLoadError|dynamically imported module|Failed to fetch dynamically imported module/i.test(
      error.name + ' ' + error.message,
    );
  }

  /** Prevents infinite reload loops — only auto-reload once per session. */
  private canReload(): boolean {
    return !sessionStorage.getItem(CHUNK_RELOAD_KEY);
  }

  /**
   * Cleanup subscriptions when the service is destroyed (app lifetime).
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
